import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';
import { isCorrectOpenResponse } from 'utils/challenge';
import { INCIDENT_TAG_META, IncidentTagChip, incidentTagMeta } from '../shared/criticalIncidentsUi';

const parseType = (type) => {
  switch (type) {
    case 'bot_message': return 'OKO'
    case 'speech': return 'Speaker'
    case 'event': return '[Event]'
    case 'status': return '[System]'
    case 'system': return '[System]'
    default: return type
  }
}

const capitalize = (str) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
};

// Outlasts the tab switch's own tab-bar scroll (100ms) and the pane's fade-in.
const HIGHLIGHT_SCROLL_DELAY_MS = 300;

const MNP_STRATEGY_LABELS = {
  A: 'Reframe Struggle & Capability',
  B: 'Celebrate Effort & Strategy',
  C: 'Normalize Mistakes & Growth',
  D: 'Build Math Identity',
};

export const Transcript = ({ transcript, challengeData, speechTags = [], betterHints = [], highlightQuote = null }) => {
  // The deep-link anchor: an incident names its quote by the same
  // `timestamp|content` composite the tag highlights use. Keyed by the quote's
  // value, not a one-shot flag, so picking a second incident scrolls again.
  const highlightRef = useRef(null);
  const scrolledToRef = useRef(null);
  highlightRef.current = null;

  const isHighlighted = (event) => {
    if (!highlightQuote?.text || event.type !== 'speech') return false;
    if (event.content !== highlightQuote.text) return false;
    return !highlightQuote.timestamp || event.timestamp === highlightQuote.timestamp;
  };

  useEffect(() => {
    if (!highlightQuote || !highlightRef.current) return;
    const quoteKey = `${highlightQuote.timestamp ?? ''}|${highlightQuote.text ?? ''}`;
    if (scrolledToRef.current === quoteKey) return;
    scrolledToRef.current = quoteKey;
    const el = highlightRef.current;
    // Deferred past the tab switch: when the quote is picked on the Critical
    // Incidents tab this pane is still fading in (scrollIntoView on a hidden
    // element is a no-op) and switchTab scrolls the tab bar into view 100ms
    // later, which would override an immediate scroll. No cleanup on purpose —
    // a re-render inside the delay must not cancel the one scroll.
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), HIGHLIGHT_SCROLL_DELAY_MS);
  });

  const tagsByKey = useMemo(() => {
    const map = {};
    if (!speechTags?.length) return map;
    for (const tag of speechTags) {
      const timestamp = tag.quote_timestamp ?? tag.utterance_timestamp;
      const text = tag.quote ?? tag.utterance_text;
      if (!timestamp) continue;
      const key = `${timestamp}|${text}`;
      if (!map[key]) map[key] = [];
      map[key].push(tag);
    }
    return map;
  }, [speechTags]);

  const hintsByQuestion = useMemo(() => {
    const map = {};
    if (!betterHints?.length) return map;
    for (const hint of betterHints) {
      const key = stripHtml(hint.question_prompt);
      if (!key) continue;
      if (!map[key]) map[key] = [];
      map[key].push(hint);
    }
    return map;
  }, [betterHints]);

  // Completed analyses were stored without `name` on answer rows; the student
  // id survives inside the content JSON. _participant_names maps it back and,
  // being keyed by id, keeps students who share a display name apart;
  // _participant_ids is the name-keyed map older API responses carry.
  const nameByUserId = useMemo(() => {
    const names = transcript?.analysis_json?._participant_names;
    if (names) return names;
    const ids = transcript?.analysis_json?._participant_ids || {};
    return Object.fromEntries(Object.entries(ids).map(([name, id]) => [id, name]));
  }, [transcript]);

  const answerStudentName = (content) => {
    const idx = content?.indexOf('challenge_response:') ?? -1;
    if (idx === -1) return undefined;
    try {
      const { oko_user_id } = JSON.parse(content.substring(idx + 'challenge_response:'.length));
      return Object.prototype.hasOwnProperty.call(nameByUserId, oko_user_id) ? nameByUserId[oko_user_id] : undefined;
    } catch {
      return undefined;
    }
  };

  const getPhases = () => {
    if (!transcript) return null;

    if (transcript?.session?.phases) {
      return transcript.session.phases;
    }
    if (transcript?.transcript?.session?.phases) {
      return transcript.transcript.session.phases;
    }
    if (transcript?.transcript?.phases) {
      return transcript.transcript.phases;
    }
    if (transcript?.phases) {
      return transcript.phases;
    }
    if (Array.isArray(transcript)) {
      return transcript;
    }
    if (Array.isArray(transcript?.transcript)) {
      return transcript.transcript;
    }
    return null;
  };

  const phases = getPhases();

  const formatDuration = (seconds) => {
    if (!seconds) return "";

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `(${minutes}m ${remainingSeconds}s)`;
    }
    return `(${remainingSeconds}s)`;
  };

  const cleanEventContent = (content) => {
    if (!content) return content;
    const challengeResponseIndex = content.indexOf('challenge_response:');
    if (challengeResponseIndex !== -1) {
      try {
        const jsonStr = content.substring(challengeResponseIndex + 'challenge_response:'.length).trim();
        const response = JSON.parse(jsonStr);

        const challenge = Object.values(challengeData).flatMap(item => item)?.find(c => c?.challenge?.id === response?.challenge_id);

        if (response.solution !== undefined) {
          const isCorrect = isCorrectOpenResponse(response.solution, challenge?.challenge?.solution);
          const correctnessLabel = isCorrect ? '(correct)' : '(incorrect)';
          const result = `Submitted answer ${response.solution} ${correctnessLabel}`;
          return result;
        }

        if (response.option_index !== undefined) {
          const isCorrect = challenge?.challenge?.solution_index === response.option_index;
          const correctnessLabel = isCorrect ? '(correct)' : '(incorrect)';

          if (challenge?.challenge?.answer_options) {
            const answerText = challenge.challenge.answer_options[response.option_index];
            if (answerText) {
              return `selected ${answerText} ${correctnessLabel}`;
            }
          }

          const optionLetters = ['A', 'B', 'C', 'D'];
          const optionLetter = optionLetters[response.option_index] || response.option_index;
          return `selected Option ${optionLetter}`;
        }

        return content.substring(0, challengeResponseIndex);
      } catch {
        return content.substring(0, challengeResponseIndex);
      }
    }

    return content;
  };

  const getMatchingTags = (event) => {
    if (event.type !== 'speech' || !event.timestamp) return null;
    const key = `${event.timestamp}|${event.content}`;
    const matches = tagsByKey[key];
    if (!matches?.length) return null;
    const allTags = matches.flatMap(m => m.tags || []);
    if (!allTags.length) return null;
    const first = matches[0];
    return { tags: allTags, reasoning: first?.rationale ?? first?.tag_reasoning };
  };

  const renderInlineHints = (questionText) => {
    if (!questionText) return null;
    const normalizedKey = questionText.replace(/\s+/g, ' ').trim();
    const hints = hintsByQuestion[normalizedKey];
    if (!hints?.length) return null;

    return hints.map((hint, i) => (
      <HintBlock key={i}>
        <HintHeader>
          {hint.mnp_strategy_used && (
            <StrategyBadge>
              Strategy {hint.mnp_strategy_used}
              {MNP_STRATEGY_LABELS[hint.mnp_strategy_used] && (
                <> &mdash; {MNP_STRATEGY_LABELS[hint.mnp_strategy_used]}</>
              )}
            </StrategyBadge>
          )}
          {hint.correct_answer && (
            <HintMeta>Answer: {hint.correct_answer}</HintMeta>
          )}
        </HintHeader>
        <HintText>&ldquo;{hint.hint_text}&rdquo;</HintText>
        {hint.mnp_strategy_rationale && (
          <HintRationale>Rationale: {hint.mnp_strategy_rationale}</HintRationale>
        )}
      </HintBlock>
    ));
  };

  const renderPhases = (phases) => {
    if (!phases || phases.length === 0) return null;

    let lastQuestionText = null;
    return phases.map((phase, phaseIndex) => {
      if (phase.question?.text) lastQuestionText = phase.question.text;
      const isSolution = phase.label?.toLowerCase().includes('solution');
      return (
        <Wrapper key={phaseIndex}>
          <Title>{phase.label.toUpperCase()} {phase.duration_seconds ? formatDuration(phase.duration_seconds) : ""}</Title>

          {phase.question && (
            <QuestionBox>
              <QuestionLabel>Question:</QuestionLabel>
              <Text>{phase.question.text}</Text>
            </QuestionBox>
          )}

          <List>
            {phase.events?.map((event, eventIndex) => {
              const matchedTags = getMatchingTags(event);
              const highlighted = isHighlighted(event);
              const highlightMeta = highlighted ? incidentTagMeta(matchedTags?.tags?.[0]) : null;
              const utteranceId = `utterance-${event.timestamp || phaseIndex}-${eventIndex}`;
              // Most rows carry the student under `speaker`; challenge_response rows use `name`, or on
              // older stored rows only the oko_user_id inside the content.
              const speaker = event.speaker || event.name || answerStudentName(event.content);
              return (
                <EventItem
                  key={eventIndex}
                  id={utteranceId}
                  eventType={event.type}
                  data-testid={`transcript-event-${eventIndex}`}
                  data-highlighted={highlighted ? "true" : undefined}
                  data-highlight-bg={highlightMeta?.bg}
                  $highlightBg={highlightMeta?.bg}
                  $highlightBorder={highlightMeta?.border}
                  ref={highlighted ? (el) => { if (el && !highlightRef.current) highlightRef.current = el; } : undefined}
                >
                  <strong>[{event.timestamp}]</strong>
                  <EventType>
                    {event.type === 'speech' && speaker && speaker !== 'Unknown'
                      ? capitalize(speaker)
                      : parseType(event.type)}
                  </EventType>
                  {event.type !== 'speech' && event.type !== 'bot_message' && speaker && speaker !== 'Unknown' && (
                    <Speaker>({speaker})</Speaker>
                  )}
                  : {cleanEventContent(event.content)}{matchedTags && matchedTags.tags.map((tag, i) => (
                    // A Critical Incidents tag wears the same chip it wears on
                    // that tab; anything else (admin console only) keeps the
                    // raw badge.
                    <InlineTag key={i}>
                      {INCIDENT_TAG_META[tag] ? <IncidentTagChip tag={tag} /> : <TagBadge>{tag}</TagBadge>}
                    </InlineTag>
                  ))}
                </EventItem>
              );
            })}
          </List>

          {isSolution && lastQuestionText && renderInlineHints(lastQuestionText)}
        </Wrapper>
      )
    });
  };

  return (
    <Container>
      {phases && phases.length > 0 ? (
        <div>
          {renderPhases(phases)}
        </div>
      ) : (
        <EmptyMessage>No transcript data available.</EmptyMessage>
      )}
    </Container>
  );
};

const EmptyMessage = styled.p`
  text-align: center;
  color: #6b7280;
  font-style: italic;
  padding: 2rem;
`

const ErrorText = styled.p`
  color: red;
`

const Container = styled.div`
  margin-top: 1.5rem;
`

const Wrapper = styled.div`
  padding-bottom: 1.5rem;

  &:last-child {
    border-bottom: none;
  }
`

const List = styled.ul`
  list-style: disc;
  padding-left: 1.25rem;
`

const EventItem = styled.li`
  line-height: 1.5;
  font-size: 1rem;

  ${({ $highlightBg, $highlightBorder }) => $highlightBg && css`
    background: ${$highlightBg};
    outline: 2px solid ${$highlightBorder};
    outline-offset: 2px;
    border-radius: 4px;
  `}
`

const EventType = styled.span`
  color: inherit;
  margin-left: 0.25rem;
`

const Speaker = styled.span`
  color: #374151;
  font-style: italic;
  margin-left: 0.25rem;
`

const Text = styled.p`
  margin-bottom: 0.5rem;
  line-height: 1.5;
`

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: #111827;
`

const QuestionBox = styled.div`
  border-radius: 0.375rem;
  margin-bottom: 0.75rem;
`

const QuestionLabel = styled.span`
  font-weight: 600;
  color: #1f2937;
  display: block;
  margin-bottom: 0.25rem;
`

const InlineTag = styled.span`
  margin-left: 0.375rem;
`

const TagBadge = styled.span`
  display: inline-block;
  padding: 0.0625rem 0.375rem;
  border-radius: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  background-color: #fff3e0;
  color: #e65100;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`

const HintBlock = styled.div`
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  margin-bottom: 1rem;
`

const HintHeader = styled.div`
  margin-bottom: 0.5rem;
`

const StrategyBadge = styled.div`
  display: inline-block;
  background: #7c3aed;
  color: white;
  padding: 0.125rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  margin-bottom: 0.375rem;
`

const HintMeta = styled.div`
  font-size: 0.8125rem;
  color: #6b7280;
`

const HintText = styled.p`
  font-style: italic;
  color: #1f2937;
  line-height: 1.5;
  margin: 0.5rem 0;
`

const HintRationale = styled.p`
  font-size: 0.8125rem;
  color: #92400e;
  margin-top: 0.375rem;
`
